---
title: "The AI events tab: scheduling approval queue"
description: "The AI events tab in Chosen is where AI-drafted scheduling replies wait for approval and where alerts warn you before an interview falls through."
category: "scheduling"
keywords:
  - "AI events approval queue"
  - "scheduling review queue"
  - "approve AI scheduling reply"
  - "stop scheduling assistant"
schema: "HowTo"
nav:
  section: "guides"
  order: 40
  parent: "/guides/scheduling"
---

The AI events tab, under **Interviews → AI events**, is the approval queue for the scheduling AI. AI-drafted replies wait here for your sign-off, and it surfaces alerts about interviews at risk of falling through. A pending-count badge tells you when something needs you.

## Approving a drafted reply

When the [AI email assistant](/guides/scheduling/email-assistant) drafts a response to a candidate reply, it lands here as a pending item with the candidate, the detected intent, and a confidence score. You have three options:

<Steps>
  <li>**Approve** — sends the drafted reply as written and executes the action behind it (a reschedule cancels and re-offers; a cancellation cancels the meeting).</li>
  <li>**Edit** — adjust the subject or body first, then approve. The edited version is what sends.</li>
  <li>**Reject** — discards the draft and takes no action. Use this when you'd rather handle the thread yourself.</li>
</Steps>

## Alerts it surfaces

The tab is not only an approval queue. It also raises things you'd otherwise miss:

| Alert | What it means |
| --- | --- |
| Candidate declined the invite | They declined the calendar invite. Reach out or reschedule. |
| No slot picked | The candidate got the email and hasn't chosen a time after a few days. |
| Interviewer hasn't accepted | An interviewer hasn't accepted the calendar invite an hour before the interview. |
| Question the assistant can't answer | A candidate asked something outside scheduling — it needs a human reply. |

## Automation mode and stopping a request

Every scheduling request runs in one of two automation modes. **Draft-for-approval** means every AI email waits in this queue for you. **Off** means the AI stops — no drafting, no sending, no reading replies.

The **Stop scheduling** control on a request flips it to off. It halts the outgoing emails and the AI monitoring at once. It does not cancel meetings already booked — cancel those from the meeting itself. Stop scheduling is the brake for "the AI is doing something I don't want"; it is not an undo for a confirmed interview.

<Callout type="warning" title="Don't let the badge sit">
The pending-count badge is there because the alerts are time-sensitive — a declined invite or an unaccepted interviewer is a problem you can still fix *before* the interview. Ignore the badge for a couple of days and that window closes. Ignored AI events tend to end as missed interviews.
</Callout>

## When it gets it wrong

- **A draft you don't agree with.** Edit it before approving, or reject it and reply yourself. Nothing sends until you approve.
- **An alert that's already handled.** If you sorted something outside Chosen, the alert may still show. Approving or rejecting the related item clears it.
- **The AI keeps acting after you've taken over.** Replying yourself on the thread usually signals handover, but to be certain, use **Stop scheduling** on the request — see [the email assistant](/guides/scheduling/email-assistant) for why a stray "thanks" isn't always enough.

## Limits

- The queue covers AI scheduling activity, not every interview event. Booked interviews live on the Interviews calendar.
- Acting on an item is final — an approved reply has been sent, a rejected draft is gone. There's no per-item undo.
